import { useTranslation } from "react-i18next";
import { Coins, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  cost: number;
  balance?: number | null;
  className?: string;
  inline?: boolean;
};

// Live cost badge for the model picker / generation modal.
// Green tone when affordable, red when the call would push balance below 0.
// `balance = null` ⇒ render in neutral tone (we don't know the balance yet).
export function CreditCostBadge({ cost, balance, className, inline }: Props) {
  const { t } = useTranslation();
  const after = balance == null ? null : balance - cost;
  const cantAfford = after !== null && after < 0;
  const free = cost === 0;
  const label = free
    ? t("models.freeOfCharge")
    : t("models.creditsPerCall", { count: cost, n: cost });

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium",
        free && "bg-secondary text-secondary-foreground",
        !free && !cantAfford && "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
        cantAfford && "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
        inline && "px-1.5",
        className,
      )}
    >
      {cantAfford ? <AlertCircle className="h-3 w-3" /> : <Coins className="h-3 w-3" />}
      {label}
      {after !== null && !free && (
        <span className="text-[10px] opacity-70 ml-1">
          ({t("models.balanceAfter", { n: after })})
        </span>
      )}
    </span>
  );
}

export default CreditCostBadge;
